/**
 * useEvalMode — v3.99ba / v3.99bd
 *
 * Frontend hook that exposes whether the current browser session is acting
 * as a "Field Access" public evaluator (an external AI agent, reviewer,
 * investor, or invited tester who hit one of the private slug URLs).
 *
 * v3.99bd — Two-link variant. The collector slug projects a synthetic
 * customer identity; the breeder slug projects a synthetic vendor
 * identity. Both share the same read-only mutation block.
 *
 * Activation flow:
 *   1. User visits `/field-access/{slug}` → FieldAccess.jsx page calls the
 *      backend verify endpoint, which returns `{ok, role}`.
 *   2. On verify success → sessionStorage `cf_eval_mode = "1"` AND
 *      `cf_eval_role = role` are set, then the page reloads `/`.
 *   3. Every other page reads this hook. When `active=true`, the
 *      PlatformContext overrides BAM + preview, AuthContext projects the
 *      role-appropriate synthetic user (customer → /account dashboard;
 *      vendor → /vendor dashboard), and AgeGate is skipped.
 *
 * Why sessionStorage (not localStorage):
 *   - Closing the tab clears the override. The link stays "share-only".
 *   - Doesn't follow the user across browser sessions or other tabs.
 */
import { useEffect, useState } from "react";

const STORAGE_KEY = "cf_eval_mode";
const ROLE_KEY = "cf_eval_role";

export function isEvalModeActive() {
  try {
    return typeof window !== "undefined" &&
      window.sessionStorage.getItem(STORAGE_KEY) === "1";
  } catch { return false; }
}

export function getEvalRole() {
  try {
    if (!isEvalModeActive()) return null;
    return window.sessionStorage.getItem(ROLE_KEY) || "customer";
  } catch { return null; }
}

export function activateEvalMode(role = "customer") {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, "1");
    window.sessionStorage.setItem(ROLE_KEY, role);
    window.dispatchEvent(new Event("cf-eval-mode-change"));
  } catch { /* swallow */ }
}

export function deactivateEvalMode() {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
    window.sessionStorage.removeItem(ROLE_KEY);
    window.dispatchEvent(new Event("cf-eval-mode-change"));
  } catch { /* swallow */ }
}

export default function useEvalMode() {
  const [state, setState] = useState(() => ({
    active: isEvalModeActive(),
    role: getEvalRole(),
  }));

  useEffect(() => {
    const handler = () => setState({
      active: isEvalModeActive(),
      role: getEvalRole(),
    });
    window.addEventListener("focus", handler);
    window.addEventListener("storage", handler);
    window.addEventListener("cf-eval-mode-change", handler);
    return () => {
      window.removeEventListener("focus", handler);
      window.removeEventListener("storage", handler);
      window.removeEventListener("cf-eval-mode-change", handler);
    };
  }, []);

  return state;
}
